import React, { useMemo, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { Dialog } from 'functional-foundations'
import { Typography, Checkbox, Button } from 'foundations-library/components'
import { formatPhoneNumber } from 'phone-numbers'

const jss = () => ({
    content: {
        minHeight: '370px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box'
    },
    spacer: {
        flex: 1
    },
    body: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        textAlign: 'center'
    },
    details: {
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        textAlign: 'center'
    },
    checkboxWrap: {
        margin: '20px 0 50px'
    },
    checkboxLabelHighlight: {
        fontWeight: 600
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        gap: '20px'
    }
})

const useStyles = makeStyles(jss)

type ModalType = 'brand' | 'campaign' | 'number'

interface BrandItem {
    csp_brand_id: string | number
    name: string
}

interface CampaignItem {
    identifier: string | number
    name: string
}

interface NumberItem {
    did?: string
    number?: string
    ['csp_campaign.name']?: string
}

interface PropsInterface {
    isOpen: boolean
    type: ModalType
    selectedItem: BrandItem | CampaignItem | NumberItem | null
    onClose(): void
    onDelete(): void
}

interface ModalContent {
    title: string
    firstLine: string
    secondLine: string
    checkboxLabel: React.ReactNode
}

/**
 *
 */
const ConfirmDeleteModal = ({
    isOpen,
    type,
    selectedItem,
    onClose,
    onDelete
}: PropsInterface): JSX.Element => {
    const classes = useStyles()
    const [isChecked, setIsChecked] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setIsChecked(false)
        }
    }, [isOpen])

    const modalContent = useMemo<ModalContent>(() => {
        if (type === 'brand') {
            const brand = selectedItem as BrandItem | null

            return {
                title: 'Are you sure you want to Delete this Brand?',
                firstLine: `Brand name: ${brand?.name || ''}`,
                secondLine: `Brand ID: ${brand?.csp_brand_id || ''}`,
                checkboxLabel: (
                    <Typography variant='body2' color='neutral-700'>
                        I confirm that I want to{' '}
                        <span className={classes.checkboxLabelHighlight}>Delete this Brand</span>
                        , and I understand this action cannot be undone.
                    </Typography>
                )
            }
        }

        if (type === 'campaign') {
            const campaign = selectedItem as CampaignItem | null

            return {
                title: 'Are you sure you want to Delete this Campaign?',
                firstLine: `Campaign name: ${campaign?.name || ''}`,
                secondLine: `Campaign ID: ${campaign?.identifier || ''}`,
                checkboxLabel: (
                    <Typography variant='body2' color='neutral-700'>
                        I confirm that I want to{' '}
                        <span className={classes.checkboxLabelHighlight}>Delete this Campaign</span>
                        , and I understand this action cannot be undone.
                    </Typography>
                )
            }
        }

        const numberItem = selectedItem as NumberItem | null
        const selectedNumber = formatPhoneNumber(numberItem?.did || '')
        const campaignName = numberItem?.['csp_campaign.name'] || ''

        return {
            title: 'Are you sure you want to remove this number from the campaign?',
            firstLine: selectedNumber,
            secondLine: `Campaign name: ${campaignName}`,
            checkboxLabel: (
                <Typography variant='body2' color='neutral-700'>
                    I confirm that I want to{' '}
                    <span className={classes.checkboxLabelHighlight}>Remove this Number</span>
                    , and I understand this number will remain on my account but will no longer be authorized to send messages under this campaign.
                </Typography>
            )
        }
    }, [type, selectedItem, classes.checkboxLabelHighlight])

    const handleDelete = () => {
        if (!isChecked) return
        onDelete()
    }

    return (
        <Dialog
            isOpen={isOpen}
            data-testid='confirm-delete-modal'
            style={{ height: 450 }}
        >
            <div className={classes.content}>
                <div className={classes.spacer} />

                <div className={classes.body}>
                    <Typography variant='h6' className={classes.title}>
                        {modalContent.title}
                    </Typography>

                    <div className={classes.details}>
                        <Typography variant='subtitle2' color='neutral-700'>
                            {modalContent.firstLine}
                        </Typography>

                        <Typography variant='body4' color='neutral-500'>
                            {modalContent.secondLine}
                        </Typography>
                    </div>

                    <div className={classes.checkboxWrap}>
                        <Checkbox
                            checked={isChecked}
                            onChange={(value) => setIsChecked(Boolean(value))}
                        >
                            {modalContent.checkboxLabel}
                        </Checkbox>
                    </div>

                    <div className={classes.actions}>
                        <Button
                            color='neutral'
                            onClick={onClose}
                        >
                            Cancel
                        </Button>

                        <Button
                            color='destructive'
                            disabled={!isChecked}
                            onClick={handleDelete}
                        >
                            Yes, Delete!
                        </Button>
                    </div>
                </div>

                <div className={classes.spacer} />
            </div>
        </Dialog>
    )
}

export default ConfirmDeleteModal
